import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Sparkle, PaperPlaneTilt, User } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";

const PROMPTS = [
  "Score my hottest leads and explain why.",
  "Draft a follow-up email for Atlas Retail Group.",
  "Summarise our pipeline risks this quarter.",
  "Write a one-paragraph investor update.",
];

export default function AIAssistant() {
  const [sessionId, setSessionId] = useState(() => crypto.randomUUID());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content }]);
    setBusy(true);
    try {
      const { data } = await api.post("/ai/chat", { session_id: sessionId, message: content });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
      setSessionId(data.session_id);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "I couldn't reach the model right now. Please try again." }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="ai-page">
      <div>
        <div className="overline mb-2 flex items-center gap-2"><Sparkle size={12} weight="fill" className="text-primary" /> Zippé AI</div>
        <h1 className="font-display text-4xl tracking-tight leading-none">Your business <span className="gold-text">co-pilot.</span></h1>
        <p className="text-muted-foreground text-sm mt-2 max-w-xl">Powered by GPT-5.2. Ask for drafts, summaries, scoring, or quick analyses across the CRM.</p>
      </div>

      <div className="rounded-2xl border border-border bg-card ai-glow overflow-hidden">
        <div ref={scrollRef} className="h-[480px] overflow-y-auto p-6 space-y-5" data-testid="ai-messages">
          {messages.length === 0 && (
            <div className="text-center pt-10">
              <div className="inline-flex w-12 h-12 rounded-full bg-primary/15 items-center justify-center mb-4">
                <Sparkle size={20} weight="fill" className="text-primary" />
              </div>
              <h3 className="font-display text-xl tracking-tight mb-2">How can I help?</h3>
              <p className="text-sm text-muted-foreground mb-6">Try one of these starters.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-w-xl mx-auto">
                {PROMPTS.map((p) => (
                  <button
                    key={p}
                    onClick={() => send(p)}
                    data-testid="ai-prompt-suggestion"
                    className="text-left text-sm rounded-lg border border-border bg-background hover:border-primary/50 px-3.5 py-3 transition-colors"
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          )}
          <AnimatePresence initial={false}>
            {messages.map((m, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
                className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}
                data-testid={`ai-message-${m.role}`}
              >
                <div className={`w-7 h-7 rounded-full shrink-0 flex items-center justify-center ${m.role === "user" ? "bg-muted" : "bg-primary/15 hairline-border"}`}>
                  {m.role === "user" ? <User size={14} /> : <Sparkle size={14} weight="fill" className="text-primary" />}
                </div>
                <div className={`max-w-[78%] rounded-xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-background border border-border"}`}>
                  {m.content}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
          {busy && (
            <div className="flex gap-3">
              <div className="w-7 h-7 rounded-full bg-primary/15 hairline-border flex items-center justify-center">
                <Sparkle size={14} weight="fill" className="text-primary animate-pulse" />
              </div>
              <div className="bg-background border border-border rounded-xl px-4 py-3 text-sm text-muted-foreground">Thinking…</div>
            </div>
          )}
        </div>

        <div className="border-t border-border p-3 flex gap-2 bg-background/40">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Ask Zippé AI anything…"
            data-testid="ai-input"
            className="border-0 bg-transparent focus-visible:ring-0 text-sm"
          />
          <Button onClick={() => send()} disabled={busy} data-testid="ai-send" className="bg-primary text-primary-foreground">
            <PaperPlaneTilt size={14} />
          </Button>
        </div>
      </div>
    </div>
  );
}
